import { createContext, useCallback, useContext, useMemo } from "react";
import { REQUIRED_FIELD_MESSAGE } from "@/constants/validation-constant";

export interface SectionRequirement {
  /** The section must be answered before the note can be signed. */
  isRequired: boolean;
  /** The last Save & Sign found this section empty; inline errors show until the next attempt. */
  isMissing: boolean;
}

export interface SectionRequirementState extends SectionRequirement {
  /** Inline error for an empty required input, only while the section is flagged missing. */
  missingError: (value: unknown) => string | undefined;
}

const SectionRequirementContext = createContext<SectionRequirement>({
  isRequired: false,
  isMissing: false,
});

/** Lets a section accordion mark its inputs without every section component threading the flags through. */
export const SectionRequirementProvider = SectionRequirementContext.Provider;

const isBlank = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === false ||
  (typeof value === "string" && !value.trim()) ||
  (Array.isArray(value) && value.length === 0);

export const useSectionRequirement = (): SectionRequirementState => {
  const { isRequired, isMissing } = useContext(SectionRequirementContext);
  const missingError = useCallback(
    (value: unknown) =>
      isRequired && isMissing && isBlank(value)
        ? REQUIRED_FIELD_MESSAGE
        : undefined,
    [isRequired, isMissing]
  );
  return useMemo(
    () => ({ isRequired, isMissing, missingError }),
    [isRequired, isMissing, missingError]
  );
};
